import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { ChevronRight, Minus, Plus, Trash2 } from "lucide-react";
import { currency } from "@/lib/format";

/**
 * Cart side-sheet shown on the Menu tab.
 * - cart: array of {menu_item_id, name, price, image_url, quantity, notes, cartItemId}
 * - All mutation handlers are passed in for testability
 */
export default function CartSheet({
    open,
    onOpenChange,
    cart,
    subtotal,
    onChangeQuantity,
    onChangeNotes,
    onRemove,
    onSubmit,
}) {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
                <SheetHeader>
                    <SheetTitle>Your order</SheetTitle>
                </SheetHeader>
                <ScrollArea className="flex-1 -mx-6 px-6">
                    {cart.length === 0 ? (
                        <div className="text-center py-10 text-sm text-muted-foreground">Cart is empty.</div>
                    ) : (
                        cart.map((line) => (
                            <CartLine
                                key={line.cartItemId}
                                line={line}
                                onChangeQuantity={(delta) => onChangeQuantity(line.cartItemId, delta)}
                                onChangeNotes={(notes) => onChangeNotes(line.cartItemId, notes)}
                                onRemove={() => onRemove(line.cartItemId)}
                            />
                        ))
                    )}
                </ScrollArea>
                <SheetFooter className="sm:justify-stretch border-t border-border pt-4">
                    <div className="w-full">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-muted-foreground">Subtotal</span>
                            <span className="font-semibold">{currency(subtotal)}</span>
                        </div>
                        <Button
                            className="w-full h-11"
                            onClick={onSubmit}
                            disabled={!cart.length}
                            data-testid="cart-submit-order-button"
                        >
                            Send order to kitchen <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}

function CartLine({ line, onChangeQuantity, onChangeNotes, onRemove }) {
    return (
        <div className="py-3 border-b border-border/60 last:border-0">
            <div className="flex items-start gap-3">
                {line.image_url && (
                    <img src={line.image_url} alt={line.name} className="h-14 w-14 rounded-md object-cover" />
                )}
                <div className="flex-1">
                    <div className="flex items-center justify-between">
                        <div className="font-medium">{line.name}</div>
                        <Button variant="ghost" size="icon" onClick={onRemove} data-testid="cart-remove-item">
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                    <div className="text-xs text-muted-foreground">{currency(line.price)} each</div>
                    <div className="flex items-center gap-2 mt-2">
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onChangeQuantity(-1)}
                            data-testid="cart-qty-decrease"
                        >
                            <Minus className="h-3 w-3" />
                        </Button>
                        <div className="w-6 text-center text-sm" data-testid="cart-qty">
                            {line.quantity}
                        </div>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onChangeQuantity(1)}
                            data-testid="cart-qty-increase"
                        >
                            <Plus className="h-3 w-3" />
                        </Button>
                        <div className="ml-auto text-sm font-medium">{currency(line.price * line.quantity)}</div>
                    </div>
                    <Textarea
                        placeholder="Notes (e.g. no onions)"
                        value={line.notes}
                        onChange={(e) => onChangeNotes(e.target.value)}
                        className="mt-2 min-h-[60px] text-xs"
                        data-testid="cart-item-notes-textarea"
                    />
                </div>
            </div>
        </div>
    );
}
